import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { usePlan } from '../../../contexts/PlanContext';
import Sidebar from '../../../components/Sidebar';
import Settings from '../../../Settings';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import { FaPlus, FaSpinner, FaTrash, FaPaperclip, FaPaperPlane, FaDatabase } from 'react-icons/fa';
import { TbLayoutSidebarLeftCollapse, TbLayoutSidebarRightCollapse } from 'react-icons/tb';
import { FormControl, FormControlLabel, RadioGroup, Radio, Popover, Button as MUIButton} from '@mui/material';
import veloxIcon from '../../../assets/Velox-icon-color.png';
import { sendMessage, getChatMessages, deleteChat } from '../../../services/chat';
import { useVirtualChatHistory } from '../../../hooks/useVirtualChatHistory';
import VirtualScrollList from '../../../components/VirtualScrollList';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { useToast } from '../../../contexts/ToastContext';
import { fileToBase64 } from '../../../utils/fileHelpers';
import { useUsage } from '../../../hooks/useUsage';
import ErrorMessage from '../../../components/ErrorMessage';

// Module Pages
import CompassView from './CompassView';
import KnowledgeHub from './KnowledgeHub';
import Radar from '../individual/Radar';

// App styles
import '../../../App.css';

// Free Plan Dashboard
function AgentVFreeDashboard() {
  const { logout } = useAuth();
  const { planType} = usePlan();
  
  const handleLogout = () => {
    logout();
  };

  return (
    <div className="app-root">
      <Sidebar 
        onLogout={handleLogout} 
      />
      <main className="main-content">
        
        <Routes>
          <Route index element={<Navigate to="agent-v" replace />} />
          <Route path="agent-v" element={<AgentVFreeAssistant />} />
          <Route path="compass" element={<CompassView />} />
          <Route path="compass/*" element={<CompassView />} />
          <Route path="knowledge" element={<Navigate to="knowledge-hub" replace />} />
          <Route path="knowledge-hub" element={<KnowledgeHub />} />
          <Route path="knowledge-hub/*" element={<KnowledgeHub />} />
          <Route path="radar/*" element={<Radar />} />
          <Route path="settings" element={<Settings />} />
          {/* Remove the problematic catch-all route that was causing redirects */}
        </Routes>
      </main>
    </div>
  );
}

// Free Plan Agent V
function AgentVFreeAssistant() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [selectedChat, setSelectedChat] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [showHistory, setShowHistory] = useState(true);
  const { user } = useAuth();
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const { showError } = useToast();
  
  // Add new state for pagination and caching
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const messagesCache = useRef(new Map());
  const PAGE_SIZE = 20;
  const MESSAGE_CACHE_TIME = 2 * 60 * 1000; // 2 minutes

  // Add new state for chat loading
  const [isChatLoading, setIsChatLoading] = useState(false);

  // Add state for communication style, persisted in sessionStorage
  const getInitialCommStyle = () => sessionStorage.getItem('commStyle') || 'professional';
  const [commStyle, setCommStyle] = useState(getInitialCommStyle());
  const [commAnchorEl, setCommAnchorEl] = useState(null);

  // Add usage tracking
  const { checkUsage, incrementUsage } = useUsage();
  const [showMonthlyLimitOverlay, setShowMonthlyLimitOverlay] = useState(false);
  const [fiveMessageLimitError, setFiveMessageLimitError] = useState(null);
  const [streamingMessage, setStreamingMessage] = useState(null);

  // Use virtual chat history hook
  const {
    allChats: chatHistory,
    isLoading: isLoadingHistory,
    isLoadingMore: isLoadingMoreChats,
    error: historyError,
    hasMore: hasMoreChats,
    loadInitialChatHistory,
    loadMoreChats,
    addNewChat,
    removeChat,
    refreshChatHistory
  } = useVirtualChatHistory(user?.id);

  useEffect(() => {
    sessionStorage.setItem('commStyle', commStyle);
  }, [commStyle]);

  const commOptions = [
    { value: 'professional', label: 'Professional - Formal and business-oriented' },
    { value: 'casual', label: 'Casual - Friendly and conversational' },
  ];

  // Add function to check if cache is valid for messages
  const isCacheValid = (timestamp, cacheTime) => {
    return timestamp && (Date.now() - timestamp < cacheTime);
  };

  // Update loadMoreMessages with better caching
  const loadMoreMessages = useCallback(async () => {
    if (!selectedChat || isLoadingMore || !hasMoreMessages) return;
    if (messages.length === 0) return; // Don't try to load more if there are no messages
    setIsLoadingMore(true);
    try {
      const oldestMessageTimestamp = messages.length > 0 
        ? messages[messages.length - 1].timestamp 
        : new Date().toISOString();
      const cacheKey = `${selectedChat.id}-${oldestMessageTimestamp}`;
      const cachedData = messagesCache.current.get(cacheKey);
      if (cachedData && isCacheValid(cachedData.timestamp, MESSAGE_CACHE_TIME)) {
        setMessages(prev => [...prev, ...cachedData.data]);
        setHasMoreMessages(cachedData.data.length === PAGE_SIZE);
        setIsLoadingMore(false);
        return;
      }
      const response = await getChatMessages(selectedChat.id, {
        before: oldestMessageTimestamp,
        limit: PAGE_SIZE
      });
      if (Array.isArray(response)) {
        const newMessages = response;
        messagesCache.current.set(cacheKey, {
          data: newMessages,
          timestamp: Date.now()
        });
        setMessages(prev => [...prev, ...newMessages]);
        setHasMoreMessages(newMessages.length === PAGE_SIZE);
      } else {
        setHasMoreMessages(false);
      }
    } catch (error) {
      // Only show error if user explicitly tried to load more (not on initial load or empty)
      if (messages.length > 0) {
        showError('Failed to load more messages. Please try again.');
      }
    } finally {
      setIsLoadingMore(false);
    }
  }, [selectedChat, messages, isLoadingMore, hasMoreMessages, MESSAGE_CACHE_TIME]);

  // Load initial chat history
  useEffect(() => {
    if (user) {
      loadInitialChatHistory();
    }
  }, [user, loadInitialChatHistory]);

  // Show toast notification when history error occurs
  useEffect(() => {
    if (historyError) {
      showError(historyError);
    }
  }, [historyError, showError]);

  // Add scroll handler for infinite loading
  const handleScroll = useCallback(() => {
    if (!chatContainerRef.current) return;
    if (!hasMoreMessages || isLoadingMore) return; // Prevent auto-load if no more messages or already loading
    const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
    // Load more when user scrolls to 80% of the content
    if (scrollTop + clientHeight >= scrollHeight * 0.8) {
      loadMoreMessages();
    }
  }, [isLoadingMore, hasMoreMessages, loadMoreMessages]);

  // Add scroll event listener
  useEffect(() => {
    const container = chatContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  // Update message loading logic with caching
  useEffect(() => {
    if (selectedChat && selectedChat.id) {
      const loadMessages = async () => {
        setIsChatLoading(true);
        try {
          const cacheKey = `${selectedChat.id}-initial`;
          const cachedMessages = messagesCache.current.get(cacheKey);
          if (cachedMessages) {
            setMessages(cachedMessages);
            setIsChatLoading(false);
            return;
          }
          const response = await getChatMessages(selectedChat.id, { limit: PAGE_SIZE });
          if (Array.isArray(response)) {
            messagesCache.current.set(cacheKey, response);
            setMessages(response);
            setHasMoreMessages(response.length === PAGE_SIZE);
          } else {
            setMessages([]);
            setHasMoreMessages(false);
          }
        } catch (error) {
          showError('Failed to load messages');
          setMessages([]);
          setHasMoreMessages(false);
        } finally {
          setIsChatLoading(false);
        }
      };
      setMessages([]);
      setHasMoreMessages(true);
      loadMessages();
    } else if (selectedChat === null) {
      // Clear messages when no chat is selected
      setMessages([]);
      setHasMoreMessages(false);
    }
  }, [selectedChat?.id]);

  // Add useEffect to handle initial sidebar state
  useEffect(() => {
    // Set initial state based on screen width
    setShowHistory(window.innerWidth > 768);
  }, []);

  // Add window resize handler
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setShowHistory(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const createNewChat = async () => {
    try {
      // Check if user has reached monthly limit before creating new chat
      const usageCheck = await checkUsage('chats');
      if (!usageCheck.allowed) {
        setShowMonthlyLimitOverlay(true);
        return;
      }
      
      setSelectedChat(null);
      setMessages([]);
      setAttachments([]);
    } catch (error) {
      console.error('Error checking usage:', error);
      // If we can't check usage, allow creating new chat
      setSelectedChat(null);
      setMessages([]);
      setAttachments([]);
    }
  };

  const handleFileAttachment = (event) => {
    const files = Array.from(event.target.files);
    setAttachments(prev => [...prev, ...files]);
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() && attachments.length === 0) return;

    const displayAttachments = attachments.map(file => ({
      name: file.name,
      size: file.size,
      type: file.type
    }));

    const preparedAttachments = await Promise.all(
      attachments.map(async (file) => ({
        name: file.name,
        type: file.type || 'application/octet-stream',
        size: file.size,
        content: await fileToBase64(file)
      }))
    );

    const userMessage = {
      content: input,
      type: 'user',
      attachments: displayAttachments,
      timestamp: new Date().toISOString(),
      status: 'sending'
    };

    const currentInput = input;
    setInput('');
    setAttachments([]);
    setIsLoading(true);
    setFiveMessageLimitError(null);

    // Add user message immediately
    setMessages(prev => [...prev, userMessage]);
    const streamingTimestamp = new Date().toISOString();
    setStreamingMessage({
      content: '',
      type: 'assistant',
      timestamp: streamingTimestamp,
      status: 'streaming'
    });

    try {
      const response = await sendMessage(currentInput, selectedChat?.id, commStyle, {
        stream: true,
        attachments: preparedAttachments,
        onToken: (token) => {
          setStreamingMessage(prev => (
            prev ? { ...prev, content: `${prev.content || ''}${token}` } : prev
          ));
        }
      });
      if (!response || !response.chat || !response.message) {
        throw new Error('Invalid response structure from server');
      }
      const { chat, message } = response;
      
      // Update chat history
      if (!selectedChat) {
        addNewChat(chat);
        setSelectedChat(chat);
        // For new chats, we don't need to reload messages since we already have them
      } else {
        // Update existing chat in the list - handled by the hook
      }

      // Update message status
      setMessages(prev => prev.map(msg => 
        msg.timestamp === userMessage.timestamp && msg.content === userMessage.content
          ? { ...msg, status: 'sent' }
          : msg
      ));

      setStreamingMessage(null);
      setMessages(prev => [...prev, { ...message, status: 'sent' }]);
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Handle specific error messages for upgrade prompts
      if (error.message.includes('hit the message limit in this chat')) {
        setFiveMessageLimitError(error.message);
        showError(error.message);
        // Remove the latest user and assistant placeholder messages
        setMessages(prev => prev.filter(msg => 
          msg.timestamp !== userMessage.timestamp
        ));
        setStreamingMessage(null);
      } else if (error.message.includes('reached your monthly limit') || error.message.includes('exhausted the chat limit for the month')) {
        setShowMonthlyLimitOverlay(true);
        showError(error.message);
        setMessages(prev => prev.filter(msg => 
          msg.timestamp !== userMessage.timestamp
        ));
        setStreamingMessage(null);
      } else {
        // For non-usage errors, show toast notification
        showError(error.message || 'Failed to send message. Please try again.');
        
        // Update message status to failed
        setMessages(prev => prev.map(msg => 
          msg.timestamp === userMessage.timestamp && msg.content === userMessage.content
            ? { ...msg, status: 'failed', errorMessage: error.message }
            : msg
        ));
        setStreamingMessage(null);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteChat = async (chatId) => {
    try {
      await deleteChat(chatId);
      removeChat(chatId);
      if (selectedChat?.id === chatId) {
        setSelectedChat(null);
        setMessages([]);
      }
    } catch (error) {
      console.error('Error deleting chat:', error);
      showError('Failed to delete chat');
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Just now';
    const date = new Date(timestamp);
    return Number.isNaN(date.getTime()) ? 'Just now' : date.toLocaleTimeString();
  };

  const renderMessage = (message, index) => {
    const messageKey = message.id || `${message.timestamp || 'pending'}-${message.type || 'message'}-${index}`;
    const messageClass = `chat-message ${message.type} ${message.status === 'failed' ? 'error' : ''} ${message.status === 'sending' ? 'sending' : ''}`;
    
    return (
      <div key={messageKey} className={messageClass}>
        <div className="message-content">
          {message.status === 'failed' && (
            <ErrorMessage
              error={message.errorMessage || 'Failed to send message'}
              showRetry={false}
              showDismiss={false}
              variant="error"
            />
          )}
          {message.content && (
            <div className="message-text">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  code({node, inline, className, children, ...props}) {
                    const match = /language-(\w+)/.exec(className || '');
                    return !inline && match ? (
                      <SyntaxHighlighter
                        style={vscDarkPlus}
                        language={match[1]}
                        PreTag="div"
                        {...props}
                      >
                        {String(children).replace(/\n$/, '')}
                      </SyntaxHighlighter>
                    ) : (
                      <code className={className} {...props}>
                        {children}
                      </code>
                    );
                  }
                }}
              >
                {message.content}
              </ReactMarkdown>
            </div>
          )}
          {message.status === 'streaming' && !message.content && (
            <div className="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          )}
          
          {message.attachments && message.attachments.length > 0 && (
            <div className="message-attachments">
              {message.attachments.map((attachment, index) => (
                <div key={index} className="attachment-item">
                  <FaPaperclip />
                  <span>{attachment.name}</span>
                  <span className="attachment-size">
                    {(attachment.size / 1024).toFixed(1)}KB
                  </span>
                </div>
              ))}
            </div>
          )}

          {message.sources && message.sources.length > 0 && (
            <div className="message-sources">
              <div className="sources-header">
                <FaDatabase /> Sources:
              </div>
              <ul>
                {message.sources.map((source, index) => (
                  <li key={index}>{source}</li>
                ))}
        </ul>
            </div>
          )}
          
          <div className="message-timestamp">
            {formatTimestamp(message.timestamp)}
          </div>
        </div>
      </div>
    );
  };

  const renderWelcomeMessage = () => {
    if (messages.length > 0) return null;
    
    return (
      <div className="welcome-overlay">
        <div className="welcome-message">
          <h2>Hi, {user?.firstName || 'there'}!</h2>
          <p>What would you like to do?</p>
        </div>
    </div>
  );
  };

  const toggleHistory = () => {
    setShowHistory(prev => !prev);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const handleUpgradeClick = () => {
    window.location.href = '/billing';
  };

  const renderMonthlyLimitOverlay = () => {
    if (!showMonthlyLimitOverlay) return null;

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '2rem',
          borderRadius: '12px',
          maxWidth: '400px',
          textAlign: 'center',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            backgroundColor: '#fee2e2',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1rem',
            border: '2px solid #fecaca'
          }}>
            <span style={{ fontSize: '24px' }}>⚠️</span>
          </div>
          <h3 style={{ 
            marginBottom: '1rem', 
            color: '#b91c1c',
            fontSize: '1.5rem',
            fontWeight: '600'
          }}>
            Monthly Chat Limit Reached
          </h3>
          <p style={{ 
            marginBottom: '1.5rem', 
            color: '#666',
            fontSize: '1rem',
            lineHeight: '1.5'
          }}>
            You've reached your monthly limit of 10 chats. Please upgrade your plan for unlimited access.
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
            <button
              onClick={handleUpgradeClick}
              style={{
                backgroundColor: '#3b9efc',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '500',
                fontSize: '0.875rem',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#2980b9'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#3b9efc'}
            >
              Upgrade Plan
            </button>
            <button
              onClick={() => setShowMonthlyLimitOverlay(false)}
              style={{
                backgroundColor: '#e5e7eb',
                color: '#374151',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.875rem',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#d1d5db'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#e5e7eb'}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderFiveMessageLimitBanner = () => {
    if (!fiveMessageLimitError) return null;

    return (
      <ErrorMessage
        error={fiveMessageLimitError}
        onDismiss={() => setFiveMessageLimitError(null)}
        onUpgradeClick={handleUpgradeClick}
      />
    );
  };

  return (
    <div className="chat-layout">
      <div className="chat-container">
        <div className="chat-header">
          <div className="chat-title" style={{ justifyContent: 'flex-start', marginLeft: 0, textAlign: 'left', flex: 1 }}>
            <img src={veloxIcon} alt="Velox AI" className="chat-icon" />
            <span>Agent V</span>
          </div>
          <button 
            className="toggle-history-button"
            onClick={toggleHistory}
            aria-label={showHistory ? 'Hide chat history' : 'Show chat history'}
          >
            {showHistory ? <TbLayoutSidebarRightCollapse /> : <TbLayoutSidebarLeftCollapse color="#2563eb" />}
          </button>
        </div>

        <div className="chat-messages" ref={chatContainerRef}>
          {isLoadingMore && (
            <div className="loading-more-messages">
              <FaSpinner className="spinner" /> Loading more messages...
            </div>
          )}
          {isChatLoading && (
            <div className="loading-indicator">
              <FaSpinner className="spinner" /> Loading chat...
            </div>
          )}
          {!isChatLoading && renderWelcomeMessage()}
          {messages.map((message, index) => (
            renderMessage(message, index)
          ))}
          {streamingMessage && renderMessage(streamingMessage, messages.length)}
          {isLoading && !streamingMessage && (
            <div className="message assistant">
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          {/* Error messages now handled by toast notifications in top right corner */}
          {renderFiveMessageLimitBanner()}
          <div ref={messagesEndRef} />
        </div>

        <div className="chat-input-container">
          {attachments.length > 0 && (
            <div className="attachments-preview">
              {attachments.map((file, index) => (
                <div key={index} className="attachment-preview-item">
                  <FaPaperclip />
                  <span>{file.name}</span>
                  <button
                    className="remove-attachment"
                    onClick={() => removeAttachment(index)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
          
          <div className="chat-input-wrapper">
            <button 
              className="chat-input-avatar"
              onClick={() => fileInputRef.current?.click()}
              aria-label="Attach file"
            >
              <FaPaperclip />
            </button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileAttachment}
              style={{ display: 'none' }}
              multiple
            />
            {/* Communication Style Radio Group */}
            <MUIButton
              variant="outlined"
              size="small"
              sx={{ ml: 1, mr: 1, fontSize: '0.65rem', textTransform: 'none', borderRadius: 2, minWidth: 0, px: 1.5, py: 1 }}
              onClick={e => setCommAnchorEl(e.currentTarget)}
              aria-label="Select communication style"
            >
              {commOptions.find(opt => opt.value === commStyle)?.label.split(' - ')[0]}
            </MUIButton>
            <Popover
              open={Boolean(commAnchorEl)}
              anchorEl={commAnchorEl}
              onClose={() => setCommAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{ sx: { p: 2, minWidth: 260 } }}
            >
              <FormControl component="fieldset">
                <RadioGroup
                  aria-label="communication-style"
                  name="communication-style"
                  value={commStyle}
                  onChange={e => {
                    setCommStyle(e.target.value);
                    setCommAnchorEl(null);
                  }}
                >
                  {commOptions.map(opt => (
                    <FormControlLabel
                      key={opt.value}
                      value={opt.value}
                      control={<Radio size="small" />}
                      label={<span style={{ fontSize: 13 }}>{opt.label}</span>}
                      sx={{ mb: 1 }}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Popover>
            <textarea
              className="chat-input-field"
              placeholder="Type your message..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              aria-label="Message input"
            />
            <button 
              className="chat-input-send"
              onClick={handleSendMessage}
              disabled={(!input.trim() && attachments.length === 0)}
              aria-label="Send message"
            >
              <FaPaperPlane />
            </button>
          </div>
        </div>
      </div>
      
      <div className={`chat-history-sidebar ${showHistory ? 'visible' : ''}`}>
        <div className="chat-history-header">
          <h2>Chat History</h2>
        </div>
        <button 
          className="new-chat-button"
          onClick={createNewChat}
          disabled={isLoadingHistory}
        >
          <FaPlus /> New Chat
        </button>
        <div className="chat-history-list">
          {isLoadingHistory ? (
            <div className="loading-indicator">
              <FaSpinner className="spinner" /> Loading previous chats...
            </div>
          ) : historyError ? (
            <div className="error-message" style={{ padding: '12px', color: '#dc2626' }}>
              {historyError}
            </div>
          ) : !chatHistory || chatHistory.length === 0 ? (
            <div className="no-chats-message">
              No chat history yet. Start a new chat!
            </div>
          ) : (
            <VirtualScrollList
              items={chatHistory}
              itemHeight={80}
              containerHeight={400}
              overscan={5}
              hasMore={hasMoreChats}
              isLoading={isLoadingMoreChats}
              onLoadMore={loadMoreChats}
              className="virtual-chat-list"
              renderItem={(chat, index) => (
                <div
                  key={chat.id}
                  className={`chat-history-item ${selectedChat?.id === chat.id ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedChat(chat);
                    setMessages([]);
                  }}
                >
                  <div className="chat-history-item-content">
                    <h3>{chat.lastMessage || 'New Chat'}</h3>
                  </div>
                  <button
                    className="delete-chat-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteChat(chat.id);
                    }}
                  >
                    <FaTrash />
                  </button>
                </div>
              )}
            />
          )}
        </div>
      </div>  
      {renderMonthlyLimitOverlay()}
    </div>
  );
}

// Main Free Plan App Component - This is what gets imported by App.js
function AgentVFreeApp() {
  return <AgentVFreeDashboard />;
}

export default AgentVFreeApp; 